import apiService from './ApiService';
import extensionService from '@/services/ExtensionService';
import { User } from '@/models/User';
import { Company } from '@/models/Company';
import { AnyJson } from '@/@types/AnyJson';
import { CompanyUserStatus } from '@/@types/enums/CompanyUserStatus';
import { CompanyRole } from '@/@types/CompanyRole';
import { Logger } from '@/other/Logger';
import { Mutations } from '@/store/mutations';
import { Store } from 'vuex';
import { CompanyResponse } from '@/@types/responses/CompanyResponses';
import { UserResponse } from '@/@types/responses/UserResponses';
import localStorageService from '@/services/LocalStorageService';
import { clearAppContext } from '@/helpers/appContext';

const log = new Logger('AuthService');

export class AuthService {
  private endingSession: Promise<void> | null = null;
  private sessionEndFailed = false;

  protected store: Store<any>;

  setStore(store: Store<any>) {
    this.store = store;
  }

  get user(): User {
    return this.store.state.user;
  }

  setUser(data: UserResponse): User {
    this.sessionEndFailed = false;
    const user = User.parse(data);
    this.store.commit(Mutations.SET_USER, user);
    return user;
  }

  setCompany(data: CompanyResponse) {
    const company = Company.parse(data);
    this.store.commit(Mutations.SET_COMPANY, company);
    this.store.commit('setCompanyLoading', false);
    extensionService.logInUser();
  }

  isGuest(): boolean {
    return !(this.user && this.user.email);
  }

  /** The session is an HttpOnly cookie this code cannot read, so "authorized" means a user was loaded from the API. */
  isAuthorized(): boolean {
    return !!(this.user && this.user.id);
  }

  logout(): void {
    extensionService.logOutUser();
    apiService.clearLegacySession();
    this.store.commit(Mutations.SET_USER, null);
    this.store.commit(Mutations.SET_COMPANY, null);
    this.store.commit(Mutations.SET_USER_COMPANIES, null);
    localStorageService.clear();
    // H3: a stale guides context must not survive a logout, or the next
    // (unrelated) login on this tab gets hijacked into the Guides app.
    clearAppContext();
  }

  /**
   * SNAP-2389: clear local state AND leave the app.
   *
   * `logout()` on its own only forgets the token; it does not navigate. Callers that end the
   * session the user is currently holding (the header menu, and the Active Sessions page)
   * must also send them to the login screen, or they are left on a page that renders blank
   * and 401s on the next click. Kept as one method so that pairing cannot be forgotten.
   */
  logoutAndRedirect(): void {
    this.logout();
    document.location.href = `${process.env.VUE_APP_URL}/#/login`;
  }

  /**
   * User-initiated logout (SNAP-2298, SNAP-2309): revoke this device's session on the server, which
   * also clears the session cookie, then clear the local state. A 401 means the session is already
   * gone. Any other failure throws and leaves the user signed in here, because the HttpOnly session
   * may still be live and the UI must not claim otherwise. Forced logouts use `endSession()`.
   */
  async signOut(): Promise<void> {
    try {
      await apiService.logoutUser('current');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status ?? null;
      if (status !== 401) {
        log.error('Server-side logout failed, the session is still active', err);
        throw err;
      }
    }
    this.logout();
  }

  /**
   * Forced logout: a deactivated user, or a tenant host the user has no access to. Only the API can clear the
   * HttpOnly cookie, and AuthMiddleware sends anyone with a live session from the login page back to HOME, so
   * clearing the local state alone bounces between the two for ever. There is nowhere to report a failed request
   * on this path, so it clears the local state either way, and `probeSession` stops trusting the cookie until the
   * next sign-in. A 401 from the API needs none of this: the session is already over and that answer cleared the cookie.
   */
  endSession(): Promise<void> {
    if (!this.endingSession) {
      this.endingSession = this.signOut()
        .catch(() => {
          this.sessionEndFailed = true;
          this.logout();
        })
        .finally(() => {
          this.endingSession = null;
        });
    }
    return this.endingSession;
  }

  /**
   * Asks the API whether the browser's session cookie is still good. Never navigates: a 401 is the
   * normal answer for a signed-out visitor, so this reads the response instead of going through the
   * global 401 handler.
   */
  async probeSession(): Promise<boolean> {
    if (this.sessionEndFailed) return false;

    try {
      const res = await apiService.getUser();
      this.setUser(res.data.user);
      this.setCompany(res.data.user.company);
      this.store.commit(
        'setUserCompanies',
        res.data.companies.map((item: AnyJson) => Company.parse(item))
      );

      return true;
    } catch {
      return false;
    }
  }

  setUserCompanies(companies: Company[]) {
    this.store.commit('setUserCompanies', companies);
  }

  async isUserActive(): Promise<boolean> {
    try {
      const user = await this.loadUser();
      const company = user.company as Company;
      const roles: CompanyRole[] = Object.values(company.roles);
      if (company.user === user.id) {
        return true;
      }
      const activeUser: CompanyRole[] = roles.filter((item: CompanyRole) => {
        if (!item.status) return item;
        if (item.user === user.id && item.status !== CompanyUserStatus.DEACTIVATED) {
          return item;
        }
      });

      return !!activeUser.length;
    } catch (error) {
      log.error(error);
    }
  }

  async loadUser(): Promise<User> {
    try {
      const res = await apiService.getUser();
      const user = this.setUser(res.data.user);
      this.setCompany(res.data.user.company);
      this.store.commit(
        'setUserCompanies',
        res.data.companies.map((item: AnyJson) => Company.parse(item))
      );

      return user;
    } catch (err) {
      apiService.handleResponseError(err);
    }
  }
}

const authService = new AuthService();
export default authService;
